// Web push — the client half of the PWA alert channel (D-131/D-136). It manages
// the browser's push subscription and mirrors it to the backend, which does the
// VAPID-signed sending (services/api/src/web-push.ts). Web-shell only: in the
// extension build `platform.kind` is "extension" and every call no-ops, so the
// shared modules can import this unconditionally.
import { platform } from "./platform/index.js";
import { apiBase, getStoredLicense } from "./license.js";
import { storageGet, storageSet } from "./storage.js";
import { notificationStore } from "./notifications-store.js";

export type PushStatus = "unsupported" | "denied" | "off" | "on";

export function pushSupported(): boolean {
  return (
    platform.kind === "web" &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  // Ensure a worker is registered, then wait for it to be ACTIVE before we try
  // to subscribe (subscribing against a still-installing worker throws).
  try {
    await navigator.serviceWorker.register("./sw.js");
  } catch {
    /* already registered / dev — fall through to ready */
  }
  return await navigator.serviceWorker.ready;
}

/** VAPID public key is a base64url P-256 point; subscribe() wants raw bytes. */
function b64urlToBytes(b64url: string): Uint8Array {
  const p = b64url.replaceAll("-", "+").replaceAll("_", "/");
  const s = atob(p + "=".repeat((4 - (p.length % 4)) % 4));
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export async function pushStatus(): Promise<PushStatus> {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? "on" : "off";
}

/** Ask permission, subscribe, and mirror the subscription to the backend.
 *  Returns a user-facing message on any failure. Requires the user be signed in
 *  (the subscription is tied to their license account). */
export async function enablePush(): Promise<{ ok: boolean; message?: string }> {
  if (!pushSupported()) return { ok: false, message: "This browser can't do push notifications." };
  const license = await getStoredLicense();
  if (!license?.token) return { ok: false, message: "Sign in first to turn on phone alerts." };

  const base = await apiBase();
  const keyRes = await fetch(`${base}/v1/push/vapid-public`).catch(() => null);
  if (!keyRes?.ok) return { ok: false, message: "Push isn't set up on the server yet." };
  const { key } = (await keyRes.json()) as { key?: string };
  if (!key) return { ok: false, message: "Push isn't set up on the server yet." };

  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, message: "Notifications are blocked for this site." };

  const reg = await registration();
  if (!reg) return { ok: false, message: "The app isn't installed as a service worker yet." };

  let sub: PushSubscription;
  try {
    sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64urlToBytes(key) as BufferSource }));
  } catch (e) {
    return { ok: false, message: `Subscribe failed: ${e instanceof Error ? e.message : String(e)}` };
  }
  const j = sub.toJSON();
  const p256dh = j.keys?.["p256dh"];
  const auth = j.keys?.["auth"];
  if (!p256dh || !auth) return { ok: false, message: "The browser gave an incomplete subscription." };

  const res = await fetch(`${base}/v1/push/subscribe`, {
    method: "POST",
    headers: { authorization: `Bearer ${license.token}`, "content-type": "application/json" },
    body: JSON.stringify({ endpoint: sub.endpoint, keys: { p256dh, auth } }),
  }).catch(() => null);
  if (!res?.ok) return { ok: false, message: "Couldn't save the subscription. Try again." };
  return { ok: true };
}

/** Unsubscribe locally and drop the row on the backend. */
export async function disablePush(): Promise<void> {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  const license = await getStoredLicense();
  if (license?.token) {
    const base = await apiBase();
    await fetch(`${base}/v1/push/unsubscribe`, {
      method: "POST",
      headers: { authorization: `Bearer ${license.token}`, "content-type": "application/json" },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    }).catch(() => {});
  }
  await sub.unsubscribe().catch(() => {});
}

/** One armed price alert, mirrored to the server for the D-137 scan. Shape
 *  matches the client's PriceAlert so a store's alerts map serialises directly. */
export interface ArmedAlert {
  symbol: string;
  targetPrice: number;
  wasAbove: boolean;
}

/** Mirror the user's whole armed-alert set to the server so the cron scan can
 *  fire them for a closed app. Opt-in + privacy-guarded: only sends while phone
 *  alerts are actually ON (and web + signed in). A no-op otherwise. */
export async function syncArmedAlerts(alerts: ArmedAlert[]): Promise<void> {
  if (platform.kind !== "web") return;
  if ((await pushStatus()) !== "on") return; // don't send watchlist data unless opted in
  const license = await getStoredLicense();
  if (!license?.token) return;
  const base = await apiBase();
  await fetch(`${base}/v1/alerts/sync`, {
    method: "POST",
    headers: { authorization: `Bearer ${license.token}`, "content-type": "application/json" },
    body: JSON.stringify({ alerts }),
  }).catch(() => {});
}

/** Delete all of the user's armed alerts on the server (phone alerts turned off). */
export async function clearArmedAlerts(): Promise<void> {
  if (platform.kind !== "web") return;
  const license = await getStoredLicense();
  if (!license?.token) return;
  const base = await apiBase();
  await fetch(`${base}/v1/alerts/clear`, {
    method: "POST",
    headers: { authorization: `Bearer ${license.token}` },
  }).catch(() => {});
}

interface FiredAlert {
  symbol: string;
  targetPrice: number;
  wasAbove: boolean;
  firedAt: number | null;
}

/** Reconcile with the server on app open: alerts the server fired while we were
 *  away come back as `fired`. Record each in the in-app notification feed, drop
 *  it from the local armed set (so we don't re-arm/re-fire it), then ack it.
 *  Web + opted-in only. Runs before any alert sync. */
export async function reconcileFiredAlerts(): Promise<void> {
  if (platform.kind !== "web") return;
  if ((await pushStatus()) !== "on") return;
  const license = await getStoredLicense();
  if (!license?.token) return;
  const base = await apiBase();
  const res = await fetch(`${base}/v1/alerts`, { headers: { authorization: `Bearer ${license.token}` } }).catch(() => null);
  if (!res?.ok) return;
  const fired = ((await res.json()) as { fired?: FiredAlert[] }).fired ?? [];
  if (!fired.length) return;

  await notificationStore.init();
  const local = await storageGet<Record<string, ArmedAlert>>("watchlistAlerts", {});
  for (const f of fired) {
    const dir = f.wasAbove ? "fell below" : "rose above";
    // MONITOR is web nav index 2 — deep-links the notification to that page.
    await notificationStore.push(`serverfire:${f.symbol}:${f.firedAt ?? 0}`, `${f.symbol} alert`, `${f.symbol} ${dir} ${f.targetPrice}.`, 2, "priceAlert");
    delete local[f.symbol];
  }
  await storageSet("watchlistAlerts", local);
  await fetch(`${base}/v1/alerts/ack`, {
    method: "POST",
    headers: { authorization: `Bearer ${license.token}`, "content-type": "application/json" },
    body: JSON.stringify({ symbols: fired.map((f) => f.symbol) }),
  }).catch(() => {});
}

/** One live confluence setup, mirrored to the server for the D-138 scan. */
export interface LiveSetup {
  id: string;
  symbol: string;
  card: unknown; // the confluence Card (opaque here; the server runs the engine on it)
}

/** Mirror the user's live confluence setups to the server (opt-in; web + push-on). */
export async function syncLiveSetups(setups: LiveSetup[]): Promise<void> {
  if (platform.kind !== "web") return;
  if ((await pushStatus()) !== "on") return;
  const license = await getStoredLicense();
  if (!license?.token) return;
  const base = await apiBase();
  await fetch(`${base}/v1/setups/sync`, {
    method: "POST",
    headers: { authorization: `Bearer ${license.token}`, "content-type": "application/json" },
    body: JSON.stringify({ setups }),
  }).catch(() => {});
}

/** Delete all of the user's live setups on the server (phone alerts turned off). */
export async function clearLiveSetups(): Promise<void> {
  if (platform.kind !== "web") return;
  const license = await getStoredLicense();
  if (!license?.token) return;
  const base = await apiBase();
  await fetch(`${base}/v1/setups/clear`, {
    method: "POST",
    headers: { authorization: `Bearer ${license.token}` },
  }).catch(() => {});
}

interface SetupFire {
  id: number;
  symbol: string;
  verdict: string;
  label: string | null;
  fired_at: number;
}

/** On app open, pull confluence setup fires the server recorded while away,
 *  drop them into the in-app feed, and ack them. Web + opted-in only. */
export async function reconcileSetupFires(): Promise<void> {
  if (platform.kind !== "web") return;
  if ((await pushStatus()) !== "on") return;
  const license = await getStoredLicense();
  if (!license?.token) return;
  const base = await apiBase();
  const res = await fetch(`${base}/v1/setups/fires`, { headers: { authorization: `Bearer ${license.token}` } }).catch(() => null);
  if (!res?.ok) return;
  const fires = ((await res.json()) as { fires?: SetupFire[] }).fires ?? [];
  if (!fires.length) return;

  await notificationStore.init();
  for (const f of fires) {
    const name = f.label ?? "Setup";
    await notificationStore.push(`setupfire:${f.id}`, `${f.symbol} setup`, `${f.symbol} — ${name}: confluence ${f.verdict}.`, 2, "strategySignal");
  }
  await fetch(`${base}/v1/setups/fires/ack`, {
    method: "POST",
    headers: { authorization: `Bearer ${license.token}`, "content-type": "application/json" },
    body: JSON.stringify({ ids: fires.map((f) => f.id) }),
  }).catch(() => {});
}

/** Send a test push to all of the user's devices — proves the channel works. */
export async function sendTestPush(): Promise<{ ok: boolean; sent?: number; message?: string }> {
  const license = await getStoredLicense();
  if (!license?.token) return { ok: false, message: "Sign in first." };
  const base = await apiBase();
  const res = await fetch(`${base}/v1/push/test`, {
    method: "POST",
    headers: { authorization: `Bearer ${license.token}` },
  }).catch(() => null);
  if (!res?.ok) return { ok: false, message: "Test send failed." };
  const body = (await res.json()) as { sent?: number };
  return { ok: true, sent: body.sent };
}
